import { createGlobalState } from '@vueuse/core';
import type { AiLessonHelperMessage, AiLessonHelperOpenRequest } from '~/types/ai-lesson-helper.types';
import type { AiLessonHelperOpenSource, AiLessonHelperPill } from '~/enums/ai-lesson-helper.enums';

const useAiLessonHelperStore = createGlobalState(() => {
  const state = reactive<{
    historyByLesson: Record<string, AiLessonHelperMessage[]>;
    chatIdByLesson: Record<string, string>;
    token: string | null;
    openRequest: AiLessonHelperOpenRequest | null;
    isTabOpen: boolean;
  }>({
    historyByLesson: {},
    chatIdByLesson: {},
    token: null,
    openRequest: null,
    isTabOpen: false,
  });

  const token = computed(() => state.token);
  const openRequest = computed(() => state.openRequest);
  const isTabOpen = computed(() => state.isTabOpen);

  const getHistory = (lessonId: string): Readonly<AiLessonHelperMessage[]> =>
    readonly(state.historyByLesson[lessonId] ?? []) as Readonly<AiLessonHelperMessage[]>;

  const getChatId = (lessonId: string): string | null =>
    state.chatIdByLesson[lessonId] ?? null;

  const setToken = (value: string | null) => {
    state.token = value;
  };

  const setChatId = (lessonId: string, chatId: string) => {
    state.chatIdByLesson[lessonId] = chatId;
  };

  const pushMessage = (lessonId: string, message: AiLessonHelperMessage) => {
    if (!state.historyByLesson[lessonId]) {
      state.historyByLesson[lessonId] = [];
    }
    state.historyByLesson[lessonId].push(message);
  };

  const replaceMessage = (lessonId: string, id: number, message: AiLessonHelperMessage) => {
    const history = state.historyByLesson[lessonId];
    if (!history) {
      return;
    }
    const index = history.findIndex(entry => entry.id === id);
    if (index === -1) {
      return;
    }
    history.splice(index, 1, { ...message, id });
  };

  const removeMessages = (lessonId: string, ids: number[]) => {
    const history = state.historyByLesson[lessonId];
    if (!history) {
      return;
    }
    state.historyByLesson[lessonId] = history.filter(
      entry => entry.id === undefined || !ids.includes(entry.id),
    );
  };

  const clear = (lessonId: string) => {
    state.historyByLesson[lessonId] = [];
  };

  const clearChatId = (lessonId: string) => {
    delete state.chatIdByLesson[lessonId];
  };

  const requestOpen = (
    question: string | null,
    source: AiLessonHelperOpenSource,
    pillId: AiLessonHelperPill | null = null,
  ) => {
    state.openRequest = { question, source, pillId };
  };

  const clearOpenRequest = () => {
    state.openRequest = null;
  };

  const setTabOpen = (open: boolean) => {
    state.isTabOpen = open;
  };

  const reset = () => {
    state.historyByLesson = {};
    state.chatIdByLesson = {};
    state.token = null;
    state.openRequest = null;
    state.isTabOpen = false;
  };

  return {
    token,
    openRequest,
    isTabOpen,
    getHistory,
    getChatId,
    setToken,
    setChatId,
    pushMessage,
    replaceMessage,
    removeMessages,
    clear,
    clearChatId,
    requestOpen,
    clearOpenRequest,
    setTabOpen,
    reset,
  };
});

export default useAiLessonHelperStore;
